import React, { Component, ReactElement, ReactNode } from 'react';
import { getProduct } from '../shared/api/shared-app-requests';
import { logError, logInfo } from '../shared/utils/logging';
import { Offer, Product, SelectedProduct } from '../types';
import { createContext } from '../dissolve/utils';
import { REGISTERED_APPS } from '../shared/types/Apps';

type ProductsContextShape = {
  loadProduct: (handle: string) => Promise<Product>;
  removeOffersWithUnavailableProducts: (offers: Offer[]) => Promise<Offer[]>;
  noProductOffersAvailable: boolean;
  setSelectedProduct: (product: SelectedProduct | undefined) => void;
  resetSelectedProduct: () => void;
  selectedProduct?: SelectedProduct;
  productDetail: ReactNode;
  setProductDetail: (component: ReactNode) => void;
};

const [useProductsContext, ContextProvider, ContextConsumer] =
  createContext<ProductsContextShape>();

function productOfferVariants(
  product: Product | null | undefined,
  offer: Offer,
) {
  if (!product || !product.variants) {
    return [];
  }
  if (offer.items) {
    const item = offer.items.find((item) => item.item_id === product.id);
    if (item && item.item_variants.length > 0 && !offer.is_same_product_offer) {
      return product.variants.filter(
        (variant) =>
          item.item_variants.includes(variant.id) ||
          variant.title === 'Default Title',
      );
    }
  }
  return product.variants;
}

/**
 * Checks the validity of a product offer based on the provided offer details and information about availability
 * of the products and product variants.
 *
 * @param {Offer} offer - The offer object to be validated.
 * @param {Product[]} products - An array of products associated with the offer.
 * @returns {boolean} Returns true if the product offer is considered valid; otherwise, false.
 *
 */
export function isValidProductOffer(
  offer: Offer,
  products: Product[],
): boolean {
  if (offer.use_recommended_products) {
    return true;
  }

  if (!products) {
    return false;
  }

  if (!Array.isArray(products)) {
    products = [products];
  }

  const hasAvailableProducts = products.some((product) => product.available);
  const hasAvailableVariant = products.some((product) => {
    const variants = productOfferVariants(product, offer);
    if (variants) {
      return variants.some((v) => v && v.available);
    }
    return false;
  });
  return hasAvailableProducts && hasAvailableVariant;
}

interface ProductsContextProviderState {
  products: {
    [handle: string]: {
      promise: Promise<any>;
      loaded: boolean;
      data?: Product;
    };
  };
  context: ProductsContextShape;
}

interface Props {
  children: React.ReactNode;
  storefrontAccessToken: string;
}

class ProductsContextProvider extends Component<
  Props,
  ProductsContextProviderState
> {
  constructor(props: Props) {
    super(props);
    this.loadProduct = this.loadProduct.bind(this);
    this.removeOffersWithUnavailableProducts =
      this.removeOffersWithUnavailableProducts.bind(this);
    this.setProduct = this.setProduct.bind(this);
    this.setSelectedProduct = this.setSelectedProduct.bind(this);
    this.state = {
      products: {},
      context: {
        selectedProduct: undefined,
        setSelectedProduct: this.setSelectedProduct,
        loadProduct: this.loadProduct,
        removeOffersWithUnavailableProducts:
          this.removeOffersWithUnavailableProducts,
        resetSelectedProduct: () => {
          this.setSelectedProduct(undefined);
          this.state.context.setProductDetail(null);
        },
        productDetail: null,
        setProductDetail: (component: ReactElement) => {
          this.setState(({ context }) => ({
            context: { ...context, productDetail: component },
          }));
        },
        noProductOffersAvailable: false,
      },
    } as ProductsContextProviderState;
  }

  async loadProduct(handle: string) {
    if (this.state.products[handle]) {
      return this.state.products[handle].promise;
    }

    try {
      const productPromise = getProduct(
        handle,
        this.props.storefrontAccessToken,
      );

      this.setProduct(handle, {
        loading: true,
        promise: productPromise,
      });

      const product = await productPromise;

      if (!product) {
        this.setProduct(handle, { loaded: true, available: false });
        return;
      }

      product.promise = productPromise;
      product.loaded = true;

      // It's fine that recommended product has empty handle
      this.setProduct(handle, product);
      return product;
    } catch (error) {
      logError(
        'Failed to load product, setting as not available.',
        REGISTERED_APPS.ADD_TO_CART,
        error,
      );
      this.setProduct(handle, { loaded: true, available: false });
    }
  }

  checkNoProductOffersAvailable = () => {
    const { products } = this.state;

    const noProductOffersAvailable =
      Object.keys(products)?.length > 0 &&
      Object.values(products).every(
        (product) => product && product.loaded && !(product as any).available,
      );

    this.setState((prevState: ProductsContextProviderState) => {
      return {
        ...prevState,
        context: {
          ...prevState.context,
          noProductOffersAvailable,
        },
      };
    });
  };

  setProduct = (handle: string, data: any) => {
    this.setState(
      (prevState: ProductsContextProviderState) => {
        return {
          ...prevState,
          products: {
            ...prevState.products,
            [handle]: data,
          },
        };
      },
      () => this.checkNoProductOffersAvailable(),
    );
  };

  setSelectedProduct(product: SelectedProduct | undefined) {
    this.setState(({ context }) => ({
      context: { ...context, selectedProduct: product },
    }));
  }

  /**
   * Asynchronously loads product information for the given offers, then filters out
   * offers with unavailable products based on a validation function.
   * Offers that are services are not removed
   *
   * @param {Offer[]} offers - The array of offers to be processed.
   * @returns {Promise<Offer[]>} A promise that resolves to an array of offers
   *                             containing only those with available products.
   */
  async removeOffersWithUnavailableProducts(offers: Offer[]): Promise<Offer[]> {
    logInfo(
      `Filtering offers with unavailable products. Unfiltered offer IDs: [${offers
        ?.map((offer) => offer.id)
        .join(',')}]`,
    );
    const products = await this.loadProductsByHandles(offers);
    const filteredProducts = products
      .filter((product) => !!product)
      ?.map((product) => ({ ...product, loaded: true }));
    const filteredOffers = offers.filter((offer) => {
      if (!offer.is_product) {
        return true;
      }
      let productsToValidate = this.constructProductsToValidate(
        offer,
        filteredProducts,
      );
      return isValidProductOffer(offer, productsToValidate);
    });
    logInfo(
      `Filtered offer IDs [${filteredOffers
        .map((offer) => offer.id)
        .join(',')}]`,
    );
    return filteredOffers;
  }

  /*
   * Relevant for products (not services)
   */
  private loadProductsByHandles(offers: Offer[]) {
    const productPromises = offers
      .filter(
        (offer) => offer.is_product && offer.items && offer.items.length > 0,
      )
      .flatMap((offer) =>
        offer.items.map((item) => this.loadProduct(item.item_handle)),
      );
    return Promise.all(productPromises);
  }

  /*
   * Relevant for products (not services)
   */
  private constructProductsToValidate(offer: Offer, products: Product[]) {
    if (offer.is_same_product_offer && offer.parentProduct) {
      return [offer.parentProduct] as Product[];
    } else if (offer.items) {
      return offer.items
        .map((item) =>
          products?.find((product) => product?.handle === item.item_handle),
        )
        ?.filter((product) => !!product) as unknown as Product[];
    }
    return [];
  }

  render() {
    return (
      <ContextProvider value={{ ...this.state.context }}>
        {this.props.children}
      </ContextProvider>
    );
  }
}

const ProductsContextConsumer =
  ContextConsumer as React.Consumer<ProductsContextShape>;

export {
  useProductsContext,
  ProductsContextConsumer,
  ProductsContextProvider,
  productOfferVariants,
};
